import { z } from 'zod';

export const PLANOS = ['essencial', 'profissional', 'elite'] as const;
export const planoCodigoSchema = z.enum(PLANOS);
export type PlanoCodigo = z.infer<typeof planoCodigoSchema>;

/**
 * Recursos controlados por plano/adicional. Os valores (quais planos liberam o quê,
 * limites e preços) ficam no banco (tabela Plano), nunca no código.
 */
export const RECURSOS = [
  'estoque',
  'custos',
  'site_padrao',
  'site_premium',
  'nfe',
  'renave',
  'crm_basico',
  'financeiro_completo',
  'metas_comissoes',
  'assinatura_digital',
  'central_atendimento',
  'midia_ia',
  'painel_dono_completo',
  'precificador',
  'pos_venda',
  'consultor_ia',
  'bi_completo',
  'rede',
  'atendente_ia',
] as const;
export const recursoSchema = z.enum(RECURSOS);
export type Recurso = z.infer<typeof recursoSchema>;

export const limitesPlanoSchema = z.object({
  veiculosPorFilial: z.number().int().positive(),
  usuariosPorFilial: z.number().int().positive(),
  midiaIaMensal: z.number().int().nonnegative(),
  fotosPorVeiculo: z.number().int().positive(),
});
export type LimitesPlano = z.infer<typeof limitesPlanoSchema>;

/** Nome de cada recurso para quem escolhe o plano (página de planos e Ajustes › Assinatura). */
export const ROTULO_RECURSO: Record<Recurso, string> = {
  estoque: 'Estoque com fotos e importação',
  custos: 'Custos e margem de cada carro',
  site_padrao: 'Site da loja',
  site_premium: 'Site Premium com campanhas',
  nfe: 'Nota fiscal',
  renave: 'RENAVE',
  crm_basico: 'Leads e funil de vendas',
  financeiro_completo: 'Financeiro completo com Pix e DRE',
  metas_comissoes: 'Metas e comissões da equipe',
  assinatura_digital: 'Contratos com assinatura digital',
  central_atendimento: 'Central de Atendimento (WhatsApp e Instagram)',
  midia_ia: 'Fotos e textos com IA',
  painel_dono_completo: 'Painel do Dono completo',
  precificador: 'Precificador de carros',
  pos_venda: 'Pós-venda automático',
  consultor_ia: 'Assistente e consultor com IA',
  bi_completo: 'Relatórios e BI completos',
  rede: 'Rede de lojas (troca de carros entre revendas)',
  atendente_ia: 'Atendente IA no WhatsApp (responde e qualifica o lead 24 h)',
};

export const ROTULO_LIMITE: Record<keyof LimitesPlano, string> = {
  veiculosPorFilial: 'Carros por filial',
  usuariosPorFilial: 'Usuários por filial',
  fotosPorVeiculo: 'Fotos por carro',
  midiaIaMensal: 'Itens de mídia com IA por mês (fotos e textos)',
};

/** O que a página pública de planos mostra (GET /planos, sem login). */
export interface PlanoPublico {
  codigo: string;
  nome: string;
  descricao: string | null;
  precoMensalCentavos: number;
  precoFilialAdicionalCentavos: number;
  limites: LimitesPlano;
  recursos: Recurso[];
}

/** Módulo que a loja liga por cima da base (página de planos e Ajustes › Assinatura). */
export interface ModuloPublico {
  codigo: string;
  nome: string;
  descricao: string | null;
  precoMensalCentavos: number;
  porFilial: boolean;
  /** "em breve": aparece, mas não dá para ligar nem é cobrado */
  emBreve: boolean;
  recursos: Recurso[];
}

export interface PlanosPublicos {
  /** Faixas da base pelo tamanho do estoque */
  planos: PlanoPublico[];
  modulos: ModuloPublico[];
  /** Meses grátis no pagamento anual (0 = sem plano anual) */
  mesesGratisAnual: number;
  /** WhatsApp de vendas (só dígitos, com DDI) ou null se não configurado */
  whatsapp: string | null;
}

export const STATUS_ASSINATURA = ['teste', 'ativa', 'inadimplente', 'cancelada'] as const;
export type StatusAssinatura = (typeof STATUS_ASSINATURA)[number];

/** Grupos de liberação gradual (docs/01 Resiliência → Deploy). */
export const GRUPOS_LIBERACAO = ['demo', 'piloto', 'geral'] as const;
export type GrupoLiberacao = (typeof GRUPOS_LIBERACAO)[number];
export const ESTAGIOS_LIBERACAO = ['desligado', 'demo', 'piloto', 'percentual', 'todos'] as const;
export type EstagioLiberacao = (typeof ESTAGIOS_LIBERACAO)[number];
